/**
 * personalization-reviews.js
 * Viori Personalizer — Review Chunk Loader & Renderer
 *
 * Client-side module for loading precomputed review JSON chunks, merging
 * them by visitor persona, and rendering review cards into DOM containers.
 * Depends on: personalization-state.js (must load first).
 *
 * Chunk files are Shopify Liquid assets in the format:
 *   reviews-default.json
 *   reviews-hairtype-[type].json (e.g., reviews-hairtype-oily.json)
 *   reviews-concern-[concern].json (e.g., reviews-concern-thinning.json)
 *
 * Public API:
 *   VioriReviews.load(chunkKey)                   → Promise<review[]>
 *   VioriReviews.merge(chunkA, chunkB, maxCount)  → review[]
 *   VioriReviews.getChunkUrl(key)                 → string
 *   VioriReviews.renderForState(containerEl, max) → void
 *   VioriReviews.renderCards(reviews, containerEl)→ void
 *
 * Spec refs: Section 10a, 10b, 10c
 */

(function (root) {
  'use strict';

  // ── Config ─────────────────────────────────────────────────────────────────
  // CDN base URL injected by Liquid (set in theme config or header)
  // Example: window.VIORI_REVIEW_CHUNK_BASE_URL = "https://cdn.shopify.com/s/files/1/..."
  var BASE_URL = root.VIORI_REVIEW_CHUNK_BASE_URL || null;

  // Defensive: if not set, construct from document.currentScript or a fallback asset URL
  if (!BASE_URL && document.currentScript) {
    var src = document.currentScript.src;
    BASE_URL = src.substring(0, src.lastIndexOf('/') + 1);
  }

  // ── Cache ──────────────────────────────────────────────────────────────────
  // In-memory cache: chunkKey → Promise<review[]>
  // Prevents duplicate fetches of the same chunk per page load.
  var _cache = {};

  // ── Helpers ────────────────────────────────────────────────────────────────
  function _getState() {
    return root.VioriState || null;
  }

  function _log(msg, data) {
    if (root.__VIORI_DEV__) {
      console.log('[VioriReviews] ' + msg, data || '');
    }
  }

  // ── Chunk URL Builder ──────────────────────────────────────────────────────
  function getChunkUrl(key) {
    if (!BASE_URL) {
      _log('Warning: BASE_URL not set. Cannot construct chunk URL.');
      return null;
    }
    return BASE_URL + 'reviews-' + key + '.json';
  }

  // ── Fetch & Parse ──────────────────────────────────────────────────────────
  function load(chunkKey) {
    // Return cached promise if already loaded
    if (_cache[chunkKey]) return _cache[chunkKey];

    var url = getChunkUrl(chunkKey);
    if (!url) {
      _log('Skipping load: no URL for chunk', chunkKey);
      return Promise.resolve([]);
    }

    // Create fetch promise and cache it immediately
    var promise = fetch(url)
      .then(function (response) {
        if (!response.ok) {
          _log('Chunk fetch failed: ' + chunkKey + ' (' + response.status + ')');
          return [];
        }
        return response.json();
      })
      .catch(function (err) {
        _log('Chunk fetch error: ' + chunkKey, err.message);
        return [];
      });

    _cache[chunkKey] = promise;
    return promise;
  }

  // ── Merge & Sort ───────────────────────────────────────────────────────────
  // Dedup by id, sort by relevance_score descending, cap at maxCount.
  // Spec Section 10c: photo reviews prioritized (higher relevance_score).
  function merge(chunkA, chunkB, maxCount) {
    maxCount = maxCount || 10;
    if (!Array.isArray(chunkA)) chunkA = [];
    if (!Array.isArray(chunkB)) chunkB = [];

    var seen = {};
    var merged = [];

    // Combine chunks
    var all = chunkA.concat(chunkB);

    // Dedup by id, keeping first occurrence (maintains insertion order for ties)
    for (var i = 0; i < all.length; i++) {
      var review = all[i];
      if (review && review.id && !seen[review.id]) {
        seen[review.id] = true;
        merged.push(review);
      }
    }

    // Sort by relevance_score descending (photo reviews have higher scores)
    merged.sort(function (a, b) {
      var scoreA = a.relevance_score || 0;
      var scoreB = b.relevance_score || 0;
      return scoreB - scoreA;
    });

    // Cap at maxCount
    return merged.slice(0, maxCount);
  }

  // ── Auto-Load by State ─────────────────────────────────────────────────────
  // Detects visitor's hair type and first-priority concern, loads both chunks,
  // merges them client-side.
  function loadForState() {
    var vs = _getState();
    if (!vs) return Promise.resolve([]);

    var personaKey = vs.getPersonaChunkKey(); // e.g., 'hairtype-oily'
    var concernKey = vs.getConcernChunkKey(); // e.g., 'concern-thinning'

    // Build load promises
    var promises = [];
    if (personaKey) {
      promises.push(load(personaKey));
    }
    if (concernKey) {
      promises.push(load(concernKey));
    }

    // If neither set, load default
    if (promises.length === 0) {
      return load('default');
    }

    // Merge all chunks
    return Promise.all(promises).then(function (chunks) {
      var chunkA = chunks[0] || [];
      var chunkB = chunks[1] || [];
      return merge(chunkA, chunkB, 10);
    });
  }

  // ── Card Renderer ──────────────────────────────────────────────────────────
  // Renders a single review card into HTML. Pure DOM manipulation (no innerHTML).
  function _renderCard(review) {
    var card = document.createElement('div');
    card.className = 'viori-review-card';
    card.setAttribute('data-review-id', review.id || '');

    // Stars
    var starsDiv = document.createElement('div');
    starsDiv.className = 'viori-review-stars';
    var rating = Math.round(review.rating || 0);
    for (var i = 1; i <= 5; i++) {
      var star = document.createElement('span');
      star.className = i <= rating ? 'star-filled' : 'star-empty';
      star.textContent = i <= rating ? '★' : '☆';
      starsDiv.appendChild(star);
    }
    card.appendChild(starsDiv);

    // Meta: nickname + verified badge
    var metaDiv = document.createElement('div');
    metaDiv.className = 'viori-review-meta';
    var nicknameEl = document.createElement('span');
    nicknameEl.className = 'viori-review-nickname';
    nicknameEl.textContent = review.nickname || 'Anonymous';
    metaDiv.appendChild(nicknameEl);

    if (review.has_photo) {
      var badge = document.createElement('span');
      badge.className = 'viori-verified-badge';
      badge.textContent = 'Verified Purchase';
      metaDiv.appendChild(badge);
    }
    card.appendChild(metaDiv);

    // Quote (key_quote is the main display text)
    var quoteDiv = document.createElement('div');
    quoteDiv.className = 'viori-review-quote';
    quoteDiv.textContent = review.key_quote || review.review || '';
    card.appendChild(quoteDiv);

    // Badges: hair type + concerns
    var badgeDiv = document.createElement('div');
    badgeDiv.className = 'viori-review-badges';

    if (review.hair_type && Array.isArray(review.hair_type) && review.hair_type.length > 0) {
      var htBadge = document.createElement('span');
      htBadge.className = 'viori-badge';
      htBadge.textContent = _formatLabel(review.hair_type[0]);
      badgeDiv.appendChild(htBadge);
    }

    if (review.hair_concerns && Array.isArray(review.hair_concerns) && review.hair_concerns.length > 0) {
      var concBadge = document.createElement('span');
      concBadge.className = 'viori-badge';
      concBadge.textContent = _formatLabel(review.hair_concerns[0]);
      badgeDiv.appendChild(concBadge);
    }

    if (badgeDiv.children.length > 0) {
      card.appendChild(badgeDiv);
    }

    // Photo if present
    if (review.has_photo && review.img) {
      var img = document.createElement('img');
      img.className = 'viori-review-image';
      img.src = review.img;
      img.alt = review.nickname + ' review';
      img.loading = 'lazy';
      card.appendChild(img);
    }

    return card;
  }

  // Format label: "oily" → "Oily Hair", "thinning" → "Thinning Hair"
  function _formatLabel(key) {
    var map = {
      'oily': 'Oily Hair',
      'normal': 'Normal Hair',
      'dry': 'Dry Hair',
      'dryScalpCondition': 'Dry Scalp',
      'thinning': 'Thinning Hair',
      'dandruff': 'Dandruff Care',
      'sensitiveScalp': 'Sensitive Scalp',
      'colorTreated': 'Color Treated',
      'flatHair': 'Flat Hair',
      'damagedFrizzy': 'Damaged & Frizzy',
      'noneHealthyHair': 'Healthy Hair',
    };
    return map[key] || key;
  }

  // ── Render to Container ────────────────────────────────────────────────────
  function renderCards(reviews, containerEl) {
    if (!containerEl) return;
    if (!Array.isArray(reviews)) reviews = [];

    // Clear container
    containerEl.innerHTML = '';

    // Render each card
    for (var i = 0; i < reviews.length; i++) {
      var card = _renderCard(reviews[i]);
      containerEl.appendChild(card);

      // Track review read (async, non-blocking)
      _trackReviewRead(reviews[i]);
    }
  }

  // Track review read event via VioriEvents
  function _trackReviewRead(review) {
    if (root.VioriEvents && typeof root.VioriEvents.trackReviewRead === 'function') {
      // Defer to next tick to avoid blocking render
      setTimeout(function () {
        root.VioriEvents.trackReviewRead(review.id, '');
      }, 0);
    }
  }

  // ── Main Render Entry Point ────────────────────────────────────────────────
  // Auto-detects visitor state, loads appropriate chunks, renders.
  function renderForState(containerEl, maxCount) {
    if (!containerEl) return;
    maxCount = maxCount || 4;

    // Load reviews based on state
    loadForState().then(function (reviews) {
      // Cap to maxCount for this container
      var toRender = reviews.slice(0, maxCount);
      renderCards(toRender, containerEl);
      _log('Rendered ' + toRender.length + ' reviews');
    }).catch(function (err) {
      _log('Render failed', err.message);
    });
  }

  // ── Public Interface ───────────────────────────────────────────────────────
  root.VioriReviews = {
    load: load,
    merge: merge,
    getChunkUrl: getChunkUrl,
    renderForState: renderForState,
    renderCards: renderCards,
    _debug: {
      cache: _cache,
      log: _log,
    },
  };

}(window));
